// marketingTokens.js
//
// Design tokens shared across the unauthenticated marketing surface
// (home, about, book-demo, etc.). Kept in sync with the design tokens
// defined inline in src/components/LoginPage.jsx — when we eventually
// merge those, this file is the single source of truth.

export const BRAND = {
  // Primary brand blues — used for headings, primary CTAs on light
  // backgrounds, and the deep-blue sections of the marketing site.
  blueDeep:   '#0F2A5F',
  blueMid:    '#1E3A8A',
  blueBrand:  '#1B2F6B',
  blueUi:     '#3B82F6',
  blueSoft:   '#60A5FA',
  blueGhost:  '#DBEAFE',   // very light blue backgrounds / accents

  // Phoenix orange — used for the primary marketing CTA ("Book a demo",
  // "Start free trial"). Used sparingly so it pops.
  orange:     '#F97316',
  orangeDark: '#EA580C',
  orangeSoft: '#FFEDD5',   // light orange backgrounds for callouts

  // Surface colors for the light marketing pages.
  bgWhite:    '#FFFFFF',
  bgLight:    '#F8FAFC',
  bgLighter:  '#F1F5F9',

  // Text
  textHead:   '#0F172A',
  textBody:   '#1E293B',
  textMuted:  '#64748B',
  textFaint:  '#94A3B8',
  textOnDark: '#E2E8F0',

  // Borders
  border:     '#E2E8F0',
  borderSoft: '#F1F5F9',
};

// Route helpers — single source of truth for the unauthenticated routes.
// AppWrapper reads these via getMarketingRoute() to dispatch.
export const ROUTES = {
  home:     '#/',
  features: '#/features',     // anchor on home
  pricing:  '#/pricing',      // standalone page (was anchor on home)
  about:    '#/about',
  security: '#/security',     // standalone page — security & trust
  login:    '#/login',
  signup:   '#/signup',       // alias of #/login with mode=signup
  bookDemo: '#/book-demo',
  contact:  '#/contact',      // anchor on home or future contact page
};

// Marketing-side hash routing. Pure function so it's easy to test.
// Recognized routes return their canonical key; anything else (including
// no hash) returns 'home'.
export function parseMarketingRoute(hash) {
  const h = (hash || '').replace(/^#\/?/, '').toLowerCase().trim();
  if (!h)                              return 'home';
  if (h === 'about')                   return 'about';
  if (h === 'pricing')                 return 'pricing';    // standalone page
  if (h === 'security' || h === 'trust') return 'security'; // standalone page
  if (h === 'book-demo' || h === 'demo') return 'bookDemo';
  if (h === 'login' || h === 'signin')  return 'login';
  if (h === 'signup')                   return 'signup';
  if (h.startsWith('features'))        return 'features';   // anchor on home
  if (h.startsWith('contact'))         return 'contact';    // anchor on home
  return 'home';
}

// Smoothly scroll to a section by id (used by the in-page anchors on
// the home page). Falls back to instant scroll if smooth is not
// supported (mainly older mobile browsers).
export function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch {
    el.scrollIntoView();
  }
}

// Navigate to a marketing route — wraps window.location.hash so callers
// don't have to remember the leading "#".
export function goTo(routeKey) {
  const target = ROUTES[routeKey];
  if (!target) return;
  // Anchor-on-home routes: navigate to home, then scroll. (pricing
  // used to be one of these — it now has its own page.)
  if (routeKey === 'features' || routeKey === 'contact') {
    if (window.location.hash !== '#/') {
      window.location.hash = '#/';
      // Defer the scroll until the next paint so the home renders first.
      setTimeout(() => scrollToSection(routeKey), 80);
    } else {
      scrollToSection(routeKey);
    }
    return;
  }
  window.location.hash = target;
  // Bring the new page to the top.
  window.scrollTo(0, 0);
}
